import { useEffect, useState } from 'react';
import { supabase } from './supabase';
import type { Property } from '../types/types';

// USD ⇄ GTQ display conversion for property prices.
//
// `price` is ALWAYS the USD amount: the committed asking price for USD-listed
// properties, or a daily-refreshed Banguat mirror for GTQ-listed ones (see
// sync-exchange-rate edge fn). `price_gtq` is the committed Q amount when
// currency === 'GTQ' — Q display shows that exact number, never a round-trip.
// The rate comes from system_settings (populated by the daily cron); while it
// is NULL everything fails open to USD-only display.

export type DisplayCurrency = 'USD' | 'GTQ';

// Module-level cache — one fetch per page load no matter how many cards mount.
let cachedRate: number | null = null;
let ratePromise: Promise<number | null> | null = null;

function fetchRate(): Promise<number | null> {
    if (!ratePromise) {
        ratePromise = (async () => {
            if (!supabase) return null;
            const { data, error } = await supabase
                .from('system_settings')
                .select('exchange_rate_gtq')
                .single();
            if (error || !data?.exchange_rate_gtq) return null;
            cachedRate = Number(data.exchange_rate_gtq);
            return cachedRate;
        })();
    }
    return ratePromise;
}

/** Banguat USD→GTQ rate (Q per $1), or null while unavailable. */
export function useGtqRate(): number | null {
    const [rate, setRate] = useState<number | null>(cachedRate);
    useEffect(() => {
        if (cachedRate === null) fetchRate().then(setRate);
    }, []);
    return rate;
}

const STORAGE_KEY = 'crm_currency';
const CHANGE_EVENT = 'crm-currency-change';

function storedCurrency(): DisplayCurrency {
    try {
        return localStorage.getItem(STORAGE_KEY) === 'GTQ' ? 'GTQ' : 'USD';
    } catch {
        return 'USD';
    }
}

/**
 * Write the display currency and notify every mounted hook. Exported so the
 * account-level preference (profiles.ui_preferences.display_currency) can be
 * applied at login — AuthContext calls this when the profile loads.
 */
export function applyDisplayCurrency(c: DisplayCurrency) {
    try {
        localStorage.setItem(STORAGE_KEY, c);
    } catch { /* private mode */ }
    window.dispatchEvent(new Event(CHANGE_EVENT));
}

/** Current $/Q preference outside React (share flows read it at mint time). */
export function getDisplayCurrency(): DisplayCurrency {
    return storedCurrency();
}

/**
 * Site-wide display currency preference, synced across every mounted
 * component via a window event (the toggle lives in the list header while
 * cards/detail render prices independently).
 */
export function useDisplayCurrency(): [DisplayCurrency, (c: DisplayCurrency) => void] {
    const [currency, setCurrency] = useState<DisplayCurrency>(storedCurrency);
    useEffect(() => {
        const onChange = () => setCurrency(storedCurrency());
        window.addEventListener(CHANGE_EVENT, onChange);
        return () => window.removeEventListener(CHANGE_EVENT, onChange);
    }, []);
    return [currency, applyDisplayCurrency];
}

export function formatUsd(amount: number): string {
    return '$' + Math.round(amount).toLocaleString('en-US');
}

export function formatGtq(amount: number): string {
    return 'Q' + Math.round(amount).toLocaleString('es-GT');
}

/**
 * Property asking price in the chosen display currency. Falls back to USD
 * when the rate is unavailable (except GTQ-listed properties, whose exact Q
 * amount needs no rate).
 */
export function formatPropertyPrice(
    p: Pick<Property, 'price' | 'currency' | 'price_gtq'>,
    display: DisplayCurrency,
    rate: number | null
): string {
    const usd = p.price ?? 0;
    if (display === 'GTQ') {
        if (p.currency === 'GTQ' && p.price_gtq) return formatGtq(p.price_gtq);
        if (rate) return formatGtq(usd * rate);
    }
    return formatUsd(usd);
}
